import { FC, useEffect, useState, useRef, useMemo } from 'react'
import { Collapse, SubPanel } from './components'
import useRequest, { RequestTypes } from '../hooks/useRequest'
import {
    GreyButton,
    CustomModal,
    IconButton,
    DropdownSelector,
    DeleteModal,
    DocumentCard,
    Panel,
} from '../ui'
import { Export } from '../../assets/icons'

import style from './style.m.less'
import { useTranslation } from 'react-i18next'
import { Form, Input, Spin } from 'antd'
import useUploadFile from '../hooks/useUploadFile'
import classnames from 'classnames'
import useDownLoadFile from '../hooks/useDownloadFile'
import { useAppSelector } from '../../store/hooks'
import { EUserRole } from '../../constants/approval'
import { useLocation } from 'react-router-dom'
import { VideoModal, VideoModalRef, FileView } from './components'

const objDocs = {
    1: 'docs',
    2: 'prices',
    3: 'signUp',
    4: 'usefulInfo',
    101: 'forms',
    102: 'manual',
    104: 'other',
    105: 'personal',
    111: 'presentations',
    112: 'articles',
    113: 'researches',
    114: 'videos',
}

export const Documents: FC = () => {
    const { t } = useTranslation()

    const options = [
        { id: 101, value: t('documents.forms') },
        { id: 102, value: t('documents.manual') },
        { id: 104, value: t('documents.other') },
        { id: 105, value: t('documents.personal') },
        { id: 2, value: t('documents.prices') },
        { id: 3, value: t('documents.signUp') },
        { id: 111, value: t('documents.presentations') },
        { id: 112, value: t('documents.articles') },
        { id: 113, value: t('documents.researches') },
        { id: 114, value: t('documents.videos') },
    ]

    const userRole = useAppSelector((state) => state.user.data.user_role)
    const isAdmin = userRole === EUserRole.Administrator || userRole === EUserRole.SuperAdmin

    const { downloadFile, loadingDownLoad, downloadedId } = useDownLoadFile()

    const [form] = Form.useForm()
    const [showDocModal, setShowDocModal] = useState(false)
    const [showLinkModal, setShowLinkModal] = useState(false)
    const [docIdToEdit, setDocIdToEdit] = useState('')
    const [type, setType] = useState('create')

    const fileInput = useRef<HTMLInputElement>(null)
    const handleClickInput = () => {
        fileInput.current?.click()
    }

    const videoModalRef = useRef<VideoModalRef>(null)

    // создание/редактирование документов
    const { fetch: postDocs, isLoading: isLoadingPost } = useRequest({
        link: `/api/content/v1/docs`,
        method: RequestTypes.post,
    })

    // в сервис контента (6) подгружаем список из одного файла (редактирование)
    // is_public=1 для публичного доступа к картинтам
    const { uploadFile, uploading, files, setFiles } = useUploadFile(6, 1, 1)
    // или у нас на форме подгружен файл или файл подгруженный для редактирования
    const isFile = files?.[0]?.id

    const file = {
        id: files?.[0]?.id,
        name: files?.[0]?.name,
        size: files?.[0]?.size,
    }

    const onFinish = async (values: any) => {
        const body = {
            ...values,
            ...(type === 'change' && { id: docIdToEdit }),
            section_id: (options.find((el) => el.value === values.section_id) || {}).id,
            document: {
                id: file.id,
            },
        }
        postDocs({
            body,
            addToLink: `/${type}`,
            successMessage: {
                title: t('detailedCard.success'),
                description: t('documents.successMessage'),
            },
            onSuccess: () => {
                getDocs({
                    body: {},
                })
                setShowDocModal(false)
                setShowLinkModal(false)
            },
            errorCodeMessage: true,
        })
    }

    const {
        fetch: getDocs,
        result: docs,
        isLoading: docsLoading,
    } = useRequest({
        link: `/api/content/v1/docs`,
        method: RequestTypes.get,
    })

    useEffect(() => {
        getDocs({
            body: {},
        })
    }, [])

    const handleAddDocuments = () => {
        setType('create')
        setFiles([])
        form.setFieldsValue({ section_id: '', link_url: '', link_name: '', comment: '' })
        setShowDocModal(true)
    }

    const handleAddLinks = () => {
        setType('create')
        setFiles([])
        form.setFieldsValue({ section_id: '', link_url: '', link_name: '', comment: '' })
        setShowLinkModal(true)
    }

    const handleClickEdit = (doc: any) => {
        const isLinkEdit = doc.link_url !== '' && doc.link_name !== ''
        if (!isLinkEdit) {
            setFiles([
                {
                    id: doc.document.id,
                    name: doc.document.file_name,
                    size: doc.document.file_size,
                },
            ])
        }
        form.setFieldsValue({
            section_id: (options.find((el) => el.id == doc.section_id) || {}).value,
            comment: doc.comment,
        })
        if (isLinkEdit) {
            form.setFieldsValue({
                link_url: doc.link_url,
                link_name: doc.link_name,
            })
        }
        setType('change')
        setDocIdToEdit(doc.id)
        if (isLinkEdit) {
            setShowLinkModal(true)
        } else {
            setShowDocModal(true)
        }
    }

    // модалка на кнопку удаления
    const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState('')

    // удаление
    const { fetch: delDoc, isLoading: deleteDocLoading } = useRequest({
        link: `/api/content/v1/docs`,
        method: RequestTypes.delete,
    })

    const handleClickDeleteDocument = () => {
        delDoc({
            body: {},
            addToLink: `?id=${showDeleteDocumentModal}`,
            successMessage: {
                title: t('detailedCard.success'),
                description: t('documents.successDeleteMessage'),
            },
            onSuccess: () => {
                getDocs({
                    body: {},
                })
            },
            errorCodeMessage: true,
        })
        setShowDeleteDocumentModal('')
    }

    const sectionDocs = useMemo(() => {
        const sectionDocuments = {}
        ;(Object.entries(objDocs) || []).forEach(([key, docName]: Array<string | number>) => {
            const sectionArr = (docs || []).filter((item: any) => item.section_id == key)
            ;(sectionDocuments as any)[docName] = sectionArr.length ? sectionArr : undefined
        })
        return sectionDocuments // { 'docs': [{section_id:1,..},..], 'npa': [{section_id:2,..}..], ...}
    }, [docs])

    const { hash } = useLocation()
    const [defaultId, setDefaultId] = useState(true)
    const [activeKey, setActiveKey] = useState<string[]>([String(objDocs[defaultId ? 1 : 2])])

    useEffect(() => {
        if (hash.includes('prices')) {
            setDefaultId(false)
        }
    }, [hash])

    useEffect(() => {
        setActiveKey([String(objDocs[defaultId ? 1 : 2])])
    }, [defaultId, objDocs])

    const handleCollapseChange = (key: string | string[]) => {
        setActiveKey(Array.isArray(key) ? key : [key])
    }

    const isVideoFile = (doc: any) => {
        const bySection = Number(doc.section_id) === 114 // "Видеоматериалы"
        const byExt = /\.(mp4|webm|ogg|mov|m4v)$/i.test(doc?.document?.file_name || '')
        return bySection || byExt
    }

    const handleFileClick = async (doc: any) => {
        if (isVideoFile(doc)) {
            const id = doc?.document?.id
            if (id) {
                await videoModalRef.current?.open(id, doc?.document?.file_name)
            }
        } else {
            downloadFile(doc.document.id, doc.document.file_name)
        }
    }

    return (
        <>
            <div className={style.mainScreen}>
                <div className={style.container}>
                    <h1 className={style.title}>{t('documents.title')}</h1>
                    <CustomModal
                        isModalVisible={showDocModal}
                        styleWrapper={{ marginBottom: '24px' }}
                        styleButtonsBlock={{ display: 'flex' }}
                        params={{
                            title:
                                type === 'create'
                                    ? t('documents.addingDocuments')
                                    : t('documents.editingDocuments'),
                            cancelTextButton: t('documents.cancel'),
                            confirmTextButton: t('documents.confirm'),
                        }}
                        children={
                            <div>
                                <Form
                                    layout="vertical"
                                    form={form}
                                    onFinish={onFinish}
                                    name="form"
                                    className={classnames(style.form, style.formDocs)}
                                    validateTrigger={['onSubmit', 'onBlur', 'onChange']}
                                >
                                    <Form.Item
                                        label={t('documents.section')}
                                        name="section_id"
                                        rules={[{ required: true, message: t('rules.required') }]}
                                    >
                                        <DropdownSelector
                                            noDefaultValue={false}
                                            options={options}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label={t('documents.doc')}
                                        style={{ width: '436px' }}
                                        name="id"
                                    >
                                        <IconButton
                                            disabled={uploading}
                                            customIconStyle={{
                                                marginTop: '4px',
                                                marginRight: '5px',
                                            }}
                                            icon={<Export />}
                                            onClick={handleClickInput}
                                            loading={uploading}
                                        >
                                            {t(
                                                isFile
                                                    ? 'documents.changeDoc'
                                                    : 'documents.downloadDoc',
                                            )}
                                        </IconButton>
                                        {isFile && (
                                            <FileView
                                                style={{ marginTop: '16px' }}
                                                title={uploading ? '' : file?.name}
                                                size={uploading ? '' : file?.size}
                                                loading={uploading}
                                            />
                                        )}

                                        <input
                                            type="file"
                                            onChange={uploadFile}
                                            ref={fileInput}
                                            style={{ display: 'none' }}
                                        />
                                    </Form.Item>

                                    {file?.id && (
                                        <Form.Item
                                            label={t('documents.comment')}
                                            style={{ width: '436px' }}
                                            name="comment"
                                            rules={[
                                                { required: true, message: t('rules.required') },
                                            ]}
                                        >
                                            <Input.TextArea
                                                style={{
                                                    minHeight: 82,
                                                    width: '436px',
                                                    border: '1px solid #D3D5DA',
                                                    borderRadius: '8px',
                                                }}
                                            />
                                        </Form.Item>
                                    )}
                                </Form>
                            </div>
                        }
                        onClose={() => setShowDocModal(false)}
                        closeAfterConfirm={false}
                        onConfirm={() => {
                            form.submit()
                        }}
                        confirmDisabled={
                            uploading || !isFile || form.getFieldValue('section_id') === ''
                        }
                    />
                    <CustomModal
                        isModalVisible={showLinkModal}
                        styleWrapper={{ marginBottom: '24px' }}
                        styleButtonsBlock={{ display: 'flex' }}
                        params={{
                            title:
                                type === 'create'
                                    ? t('documents.link.addTitle')
                                    : t('documents.link.editTitle'),
                            cancelTextButton: 'Отменить',
                            confirmTextButton: 'Сохранить',
                        }}
                        children={
                            <div>
                                <Form
                                    layout="vertical"
                                    form={form}
                                    onFinish={onFinish}
                                    name="form"
                                    className={classnames(style.form, style.formDocs)}
                                    validateTrigger={['onSubmit', 'onBlur', 'onChange']}
                                >
                                    <Form.Item
                                        label={t('documents.section')}
                                        name="section_id"
                                        rules={[{ required: true, message: t('rules.required') }]}
                                    >
                                        <DropdownSelector
                                            noDefaultValue={false}
                                            options={options}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label={t('documents.link.urlLabel')}
                                        name="link_url"
                                        rules={[{ required: true, message: t('rules.required') }]}
                                    >
                                        <Input
                                            placeholder={t('documents.link.urlPlaceholder')}
                                            // className={style.inputLong}
                                            // maxLength={1000}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label={t('documents.link.nameLabel')}
                                        name="link_name"
                                        rules={[{ required: true, message: t('rules.required') }]}
                                    >
                                        <Input
                                            placeholder={t('documents.link.namePlaceholder')}
                                            // className={style.inputLong}
                                            // maxLength={1000}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label={t('documents.link.commentLabel')}
                                        style={{ width: '436px' }}
                                        name="comment"
                                        rules={[{ required: true, message: t('rules.required') }]}
                                    >
                                        <Input.TextArea
                                            style={{
                                                minHeight: 82,
                                                width: '436px',
                                                border: '1px solid #D3D5DA',
                                                borderRadius: '8px',
                                            }}
                                        />
                                    </Form.Item>
                                </Form>
                            </div>
                        }
                        onClose={() => setShowLinkModal(false)}
                        closeAfterConfirm={false}
                        onConfirm={() => {
                            form.submit()
                        }}
                        confirmDisabled={
                            form.getFieldValue('section_id') === '' ||
                            form.getFieldValue('link_url') === '' ||
                            form.getFieldValue('link_name') === ''
                        }
                    />
                    {isAdmin && (
                        <>
                            <GreyButton
                                onClick={handleAddDocuments}
                                value={t('documents.addDocuments')}
                                customStyle={{ marginTop: '32px' }}
                            />
                            <GreyButton
                                onClick={handleAddLinks}
                                value={t('documents.link.addButton')}
                                customStyle={{ marginTop: '32px', marginLeft: '16px' }}
                            />
                        </>
                    )}
                </div>
            </div>
            <div className={style.content}>
                <div className={style.container}>
                    <Spin spinning={docsLoading}>
                        <Collapse
                            activeKey={activeKey}
                            onChange={handleCollapseChange}
                            className={style.padding}
                        >
                            {(Object.values(objDocs) || []).map((docName: any, i: number) => {
                                if (i < 4) {
                                    if (i > 0 && i < 3) {
                                        return (
                                            <Panel
                                                header={t(`documents.${docName}`)}
                                                key={docName}
                                                className={classnames(style.padding, style.collap)}
                                            >
                                                <div
                                                    className={classnames(
                                                        style.grid,
                                                        style.padding,
                                                    )}
                                                >
                                                    {(sectionDocs as any)[docName] ? (
                                                        (sectionDocs as any)[docName].map(
                                                            (doc: any, j: number) => (
                                                                <DocumentCard
                                                                    key={doc.id}
                                                                    doc={doc}
                                                                    isAdmin={isAdmin}
                                                                    onEdit={handleClickEdit}
                                                                    onDelete={(id) =>
                                                                        setShowDeleteDocumentModal(
                                                                            id,
                                                                        )
                                                                    }
                                                                    isEditingLoading={
                                                                        isLoadingPost &&
                                                                        doc.id === docIdToEdit
                                                                    }
                                                                    isDeletingLoading={
                                                                        deleteDocLoading &&
                                                                        doc.id ===
                                                                            showDeleteDocumentModal
                                                                    }
                                                                    handleFileClick={
                                                                        handleFileClick
                                                                    }
                                                                    loadingDownLoad={
                                                                        loadingDownLoad
                                                                    }
                                                                    downloadedId={downloadedId}
                                                                    width={416}
                                                                />
                                                            ),
                                                        )
                                                    ) : (
                                                        <p>{t('documents.noDocs')}</p>
                                                    )}
                                                </div>
                                            </Panel>
                                        )
                                    } else {
                                        return (
                                            <Panel
                                                header={t(`documents.${docName}`)}
                                                key={docName}
                                                className={classnames(style.padding, style.collap)}
                                            >
                                                <Collapse className={style.padding}>
                                                    {(Object.values(objDocs) || []).map(
                                                        // eslint-disable-next-line array-callback-return
                                                        (childName: any, j: number) => {
                                                            if (
                                                                (!i &&
                                                                    j > 3 &&
                                                                    j <= 7 &&
                                                                    childName) ||
                                                                (i === 3 &&
                                                                    j > 7 &&
                                                                    j <= 11 &&
                                                                    childName)
                                                            ) {
                                                                return (
                                                                    <SubPanel
                                                                        header={t(
                                                                            `documents.${childName}`,
                                                                        )}
                                                                        key={childName}
                                                                        className={classnames(
                                                                            style.padding,
                                                                            style.subCollap,
                                                                        )}
                                                                    >
                                                                        <div
                                                                            className={classnames(
                                                                                style.grid,
                                                                                style.padding,
                                                                            )}
                                                                        >
                                                                            {(sectionDocs as any)[
                                                                                childName
                                                                            ] ? (
                                                                                (
                                                                                    sectionDocs as any
                                                                                )[childName].map(
                                                                                    (
                                                                                        doc: any,
                                                                                        k: number,
                                                                                    ) => (
                                                                                        <DocumentCard
                                                                                            key={
                                                                                                doc.id
                                                                                            }
                                                                                            doc={
                                                                                                doc
                                                                                            }
                                                                                            isAdmin={
                                                                                                isAdmin
                                                                                            }
                                                                                            onEdit={
                                                                                                handleClickEdit
                                                                                            }
                                                                                            onDelete={(
                                                                                                id,
                                                                                            ) =>
                                                                                                setShowDeleteDocumentModal(
                                                                                                    id,
                                                                                                )
                                                                                            }
                                                                                            isEditingLoading={
                                                                                                isLoadingPost &&
                                                                                                doc.id ===
                                                                                                    docIdToEdit
                                                                                            }
                                                                                            isDeletingLoading={
                                                                                                deleteDocLoading &&
                                                                                                doc.id ===
                                                                                                    showDeleteDocumentModal
                                                                                            }
                                                                                            handleFileClick={
                                                                                                handleFileClick
                                                                                            }
                                                                                            loadingDownLoad={
                                                                                                loadingDownLoad
                                                                                            }
                                                                                            downloadedId={
                                                                                                downloadedId
                                                                                            }
                                                                                            width={
                                                                                                396
                                                                                            }
                                                                                        />
                                                                                    ),
                                                                                )
                                                                            ) : (
                                                                                <p>
                                                                                    {t(
                                                                                        'documents.noDocs',
                                                                                    )}
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    </SubPanel>
                                                                )
                                                            }
                                                        },
                                                    )}
                                                </Collapse>
                                            </Panel>
                                        )
                                    }
                                }
                            })}
                        </Collapse>
                    </Spin>
                </div>
                <DeleteModal
                    isModalVisible={!!showDeleteDocumentModal}
                    headerText={t('documents.delDoc')}
                    messageText={`${t('documents.delDocQuestion')} “${
                        (docs || []).find((d: any) => d.id == showDeleteDocumentModal)?.document
                            ?.file_name || ''
                    }” ?`}
                    onCancel={() => setShowDeleteDocumentModal('')}
                    onDelete={() => handleClickDeleteDocument()}
                />
                <VideoModal ref={videoModalRef} />
            </div>
        </>
    )
}

export default Documents
